import { z } from 'zod';
import { IpAddress, IsoTime } from './basics.js';
import catalogFile from './bot-catalog/bots.json' with { type: 'json' };
/**
 * The bot catalog (knowledge doc section 8.7; skill `web-traffic.md` §7; P6.2).
 *
 * Which bots Bartizo can name, what category each is, the user-agent text that
 * identifies it, and how its addresses can be checked. **Data, not code**: adding
 * a bot is an entry in `bot-catalog/bots.json` and an example user agent in the
 * agent's classifier fixtures, never a change to a module (section 18's recipe).
 * The file is versioned by `revision`, so a newer catalog from the API can replace
 * the one an agent was built with (P6.3).
 *
 * A user agent proves nothing: anybody can send `Googlebot`. The catalog only
 * names what a request *claims*; `verify` is how P6.4 checks the claim, and a
 * failed check makes the request a `fake_bot`.
 */
/**
 * Rule 18: a literal, case-insensitive substring — never a regular expression —
 * from a short list of characters, so a generator can escape it into Nginx or
 * Apache configuration (P6.6) and nothing typed can become a slow pattern.
 */
export const BotPattern = z
    .string()
    .min(3)
    .max(100)
    .regex(/^[A-Za-z0-9 ._\-/()+:]+$/, 'letters, digits, spaces and . _ - / ( ) + : only');
/** What a catalog entry can be. */
export const BotCategory = z.enum([
    'search',
    'ai',
    'seo',
    'social',
    'monitoring',
    'feed',
    'automation',
    'other_bot',
]);
/**
 * Every category a request can end up in: a catalog category, `fake_bot` once a
 * claimed bot fails its check (P6.4), or `browser` — "likely human", which a
 * script can fake as easily as it can fake a bot.
 */
export const TrafficCategory = z.enum([...BotCategory.options, 'fake_bot', 'browser']);
export const BotId = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, 'lowercase letters, digits, _ and -');
/** A lowercase DNS name a verified bot's reverse lookup must end in. */
const BotDomain = z
    .string()
    .max(253)
    .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'a lowercase domain name, such as googlebot.com');
/**
 * How a claim is checked (skill `web-traffic.md` §8), in this order:
 *
 * - `ranges`: published address lists. **The API downloads them, never the
 *   agent** (P6.4), and sends the ranges out like Cloudflare's. `google_prefixes`
 *   is the `{ "prefixes": [{ "ipv4Prefix" | "ipv6Prefix" }] }` shape Google
 *   started and Bing, OpenAI, Perplexity, Apple and Common Crawl use too.
 * - `rdns`: the address's reverse name must end in one of these domains, and that
 *   name must resolve back to the same address.
 *
 * No `verify` at all means the claim cannot be checked: the request keeps the
 * bot's name and category and is never called fake.
 *
 * **`reviewed`** is the day somebody checked these against the operator's own
 * documentation (security to-do S14). Until an entry has one, a check that does
 * not match says `unknown`, never `failed`: a wrong domain or a stale file must
 * not make a real crawler a fake bot.
 */
export const BotVerify = z
    .object({
    reviewed: z.iso.date().optional(),
    ranges: z
        .array(z
        .object({
        url: z.url({ protocol: /^https$/ }).max(300),
        format: z.enum(['google_prefixes']),
    })
        .strict())
        .min(1)
        .max(5)
        .optional(),
    rdns: z.array(BotDomain).min(1).max(10).optional(),
})
    .strict()
    .refine((verify) => verify.ranges !== undefined || verify.rdns !== undefined, {
    message: 'name ranges or rdns, or leave verify out',
});
export const BotCatalogEntry = z
    .object({
    id: BotId,
    /** As the Traffic page shows it, and the `ua_family` a request of this bot counts under. */
    name: z.string().min(1).max(64),
    /** Who runs it: Google, OpenAI, Ahrefs. */
    operator: z.string().min(1).max(64),
    category: BotCategory,
    /**
     * Literal, case-insensitive substrings; any one identifies the bot. A user
     * agent matching several entries goes to the match that starts earliest
     * (P6.2's classifier): `Feedly/1.0 (… like FeedFetcher-Google)` is Feedly.
     */
    ua_patterns: z.array(BotPattern).min(1).max(20),
    verify: BotVerify.optional(),
    /** What its operator says about robots.txt. Shown, never enforced. */
    respects_robots: z.boolean(),
    /** P6.6's starting point for this bot's policy. */
    default_policy: z.enum(['allow', 'rate_limit', 'block']).default('allow'),
})
    .strict();
/**
 * The generic bot test for clients the catalog does not name (skill §7, rule 4).
 * A user agent is a bot when one of its words ends in a `suffixes` entry
 * (`FooBot/1.0`, `SomeCrawler`), unless the word is an `exceptions` entry —
 * `cubot` is a phone maker whose name appears in ordinary browsers' user agents.
 */
export const GenericBotRule = z
    .object({
    suffixes: z
        .array(z.string().regex(/^[a-z]{3,20}$/))
        .min(1)
        .max(20),
    exceptions: z.array(z.string().regex(/^[a-z0-9]{3,40}$/)).max(200),
})
    .strict();
export const BotCatalog = z
    .object({
    /** Increases with every change; a larger one is newer. */
    revision: z.number().int().positive(),
    updated: z.iso.date(),
    bots: z.array(BotCatalogEntry).min(1).max(500),
    generic: GenericBotRule,
})
    .strict()
    .superRefine((catalog, ctx) => {
    const ids = new Set();
    const patterns = new Map();
    for (const [index, bot] of catalog.bots.entries()) {
        if (ids.has(bot.id)) {
            ctx.addIssue({ code: 'custom', path: ['bots', index, 'id'], message: `${bot.id} twice` });
        }
        ids.add(bot.id);
        for (const pattern of bot.ua_patterns) {
            const key = pattern.toLowerCase();
            const owner = patterns.get(key);
            if (owner !== undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['bots', index, 'ua_patterns'],
                    message: `"${pattern}" is already ${owner}'s`,
                });
            }
            patterns.set(key, bot.id);
        }
    }
});
/**
 * The catalog this build ships. Parsed when first imported, so a broken
 * `bots.json` stops everything at start — and a test — rather than classifying
 * with half a catalog.
 */
export const BOT_CATALOG = BotCatalog.parse(catalogFile);
/** Every published list the catalog names, once each: what the API fetches (P6.4). */
export const rangeSourcesOf = (catalog) => [
    ...new Set(catalog.bots.flatMap((bot) => (bot.verify?.ranges ?? []).map((range) => range.url))),
].sort();
/**
 * One checked claim (section 8.7; P6.4), from the agent to
 * `POST /v1/ingest/bot-verifications`, and kept in `bot_verifications` for the
 * fleet (section 16).
 *
 * - `verified`: the address is on the operator's published list, or its reverse
 *   name is in the operator's domain and resolves back to it.
 * - `failed`: it is not, and the operator's data has been reviewed — a fake bot.
 * - `unknown`: the check could not be completed — DNS timed out or failed, no
 *   list was available, or the data is not reviewed yet. Never `failed`.
 */
export const BotVerificationResult = z.enum(['verified', 'failed', 'unknown']);
/**
 * How long a result stands (section 8.7): a verified address a day, a failed one
 * six hours, and an unknown one — DNS failed, no list yet — a quarter of an hour,
 * so it is tried again soon.
 */
export const VERIFICATION_TTL_MS = {
    verified: 24 * 60 * 60_000,
    failed: 6 * 60 * 60_000,
    unknown: 15 * 60_000,
};
export const BotVerification = z
    .object({
    ip: IpAddress,
    claimedBot: BotId,
    result: BotVerificationResult,
    method: z.enum(['ip_ranges', 'rdns']),
    /** The reverse name that settled it, when reverse DNS did. */
    hostname: z
        .string()
        .min(1)
        .max(253)
        .regex(/^[a-z0-9.-]+$/, 'a lowercase host name')
        .optional(),
    checkedAt: IsoTime,
})
    .strict();
export const BotVerificationBatch = z
    .object({ verifications: z.array(BotVerification).min(1).max(500) })
    .strict();
export const BotVerificationReceipt = z.object({ stored: z.number().int().nonnegative() });
/** The catalog entry with this id, if there is one. */
export const botById = (catalog, id) => catalog.bots.find((bot) => bot.id === id);
//# sourceMappingURL=bots.js.map